'use client'

import { type AppStore, makeStore } from '@/lib/store'
import { createContext, useContext, useRef } from 'react'
import { Provider } from 'react-redux'
import { type Persistor, persistStore } from 'redux-persist'
import { PersistGate } from 'redux-persist/integration/react'

export const dynamic = 'force-dynamic'

const PersistorContext = createContext<Persistor | null>(null)

export function usePersistor() {
  const persistor = useContext(PersistorContext)
  if (!persistor) {
    throw new Error('usePersistor must be used within a StoreProvider')
  }
  return persistor
}

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const storeRef = useRef<AppStore>()
  const persistorRef = useRef<Persistor>()

  if (!storeRef.current) {
    // Clear cache before store creation if on onboard path
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/onboard')) {
      localStorage.removeItem('persist:cloudpos-root')
      localStorage.removeItem('persist:appSession')
    }
    storeRef.current = makeStore()
    persistorRef.current = persistStore(storeRef.current)
  }

  return (
    <Provider store={storeRef.current}>
      <PersistorContext.Provider value={persistorRef.current!}>
        <PersistGate loading={null} persistor={persistorRef.current!}>
          {children}
        </PersistGate>
      </PersistorContext.Provider>
    </Provider>
  )
}
